import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Palette,
  Smartphone,
  Globe,
  PenTool,
  Code2,
  Layout,
  ArrowUpRight,
  Cuboid, // Changed from Cube
  Play
} from "lucide-react";

const WhatWeDo = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const services = [
    {
      id: 1,
      title: "Social Media Design",
      description:
        "Directly relevant to businesses looking for better social content.",
      icon: <Layout className="w-5 h-5" />,
    },
    {
      id: 2,
      title: "Logo & Brand Identity",
      description: "Build a strong and memorable identity for your brand.",
      icon: <PenTool className="w-5 h-5" />,
    },
    {
      id: 3,
      title: "Graphic Design",
      description:
        "Creative visuals that make your brand and social media stand out.",
      icon: <Palette className="w-5 h-5" />,
    },
    {
      id: 4,
      title: "Website Design",
      description:
        "Visually stunning responsive websites that drive business growth.",
      icon: <Globe className="w-5 h-5" />,
    },
    {
      id: 5,
      title: "Packaging Design",
      description:
        "A strong differentiator for food, beauty, and product brands.",
      icon: <Cuboid className="w-5 h-5" />,
    },
    {
      id: 6,
      title: "Content Creation",
      description:
        "Engaging content that resonates with your target audience.",
      icon: <Play className="w-5 h-5" />,
    },
  ];

  const stats = [
    { value: "50+", label: "Projects Delivered" },
    { value: "20+", label: "Brands Designed" },
    { value: "30+", label: "Websites Designed" },
    { value: "10+", label: "Industries Served" },
    { value: "100%", label: "Client Satisfaction" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };
  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 12 },
    },
  };

  return (
    <section
      id="services"
      className="relative bg-[#F7F7F7]"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {/* Rectangular tab cutout from hero into section */}
      <div
        className="w-full bg-transparent absolute left-0"
        style={{ lineHeight: 0, marginTop: -50 }}
      >
        <svg
          viewBox="0 0 900 60"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: "block", width: "100%", height: 60 }}
        >
          <rect x="0" y="0" width="900" height="60" fill="transparent" />
          <path
            d="M220,60 Q245,60 272,8 Q278,0 290,0 L610,0 Q622,0 628,8 Q655,60 680,60 Z"
            fill="#F7F7F7"
          />
        </svg>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <p
            className="text-xl font-semibold tracking-[0] uppercase mb-3"
            style={{
              display: "inline-block",
              background:
                "linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            What We Do
          </p>
          <h2
            className="text-4xl md:text-4xl font-bold text-gray-900"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Turning "meh" brands into "wow" brands
          </h2>
        </motion.div>

        {/* Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10"
        >
          {services.map((service) => (
            <motion.div
              key={service.id}
              variants={itemVariants}
              whileHover={{ y: -6 }}
              onMouseEnter={() => setHoveredCard(service.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="bg-white rounded-2xl p-7  h-full transition-shadow duration-300 hover:shadow-xl">
                {/* Icon */}
                <div
                  className="w-13 h-13 rounded-full flex items-center justify-center mb-5 transition-all duration-300"
                  style={{
                    width: 52,
                    height: 52,
                    background:
                      hoveredCard === service.id
                        ? "linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39)"
                        : "#ebebeb",
                  }}
                >
                  <div
                    style={{
                      color: hoveredCard === service.id ? "#fff" : "#222",
                    }}
                  >
                    {service.icon}
                  </div>
                </div>

                {/* Title */}
                <h3
                  className="font-bold text-gray-900 text-base mb-0 pb-2  border-black"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {service.title}
                </h3>
                <div class="border-t border-black w-1/2"></div>

                <p
                  className="text-gray-500 text-xs leading-relaxed mt-3"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {service.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Buttons */}
       <motion.div
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.6, delay: 0.3 }}
  className="flex flex-col sm:flex-row gap-4 justify-center items-center"
>
  <motion.button
    whileHover={{ scale: 1.04 }}
    whileTap={{ scale: 0.96 }}
    onClick={() => (window.location.href = "/service")}
    className="px-8 py-2.5 font-semibold rounded-full text-sm transition-all duration-300"
    style={{
      background:
        "linear-gradient(#F7F7F7, #F7F7F7) padding-box, linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39) border-box",
      border: "1px solid transparent",
    }}
  >
    <span
      style={{
        background:
          "linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        display: "inline-block",
      }}
    >
      See all
    </span>
  </motion.button>

  <motion.button
    whileHover={{ scale: 1.04 }}
    whileTap={{ scale: 0.96 }}
    onClick={() => {
      document
        .getElementById("portfolio")
        ?.scrollIntoView({ behavior: "smooth" });
    }}
    className="px-8 py-2.5 text-black font-semibold rounded-full text-sm flex items-center gap-2 transition-opacity duration-300 hover:opacity-90"
    style={{ background: "#EBEBEB" }}
  >
    View Portfolio
    <ArrowUpRight className="w-4 h-4" />
  </motion.button>
</motion.div>

        {/* Stats */}
        {/* <div className="grid grid-cols-3 sm:grid-cols-5 gap-4 pt-10 border-t border-gray-200">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div
                className="font-bold text-gray-300 leading-none"
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "clamp(2rem, 4vw, 2.8rem)",
                }}
              >
                {s.value}
              </div>
              <div className="text-xs text-gray-400 mt-2">{s.label}</div>
            </div>
          ))}
        </div> */}
      </div>
    </section>
  );
};

export default WhatWeDo;
